/**
 * RFC-091: Partner Management Routes
 * Layout for partner management pages with AppSidebar chrome
 *
 * Access Control: Only organizations with isPartner=true can access these routes
 */

import { Suspense, useEffect } from 'react';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Loader2, Home } from 'lucide-react';
import { trpc, queryClient, trpcClient } from '@/lib/trpc';
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/app-sidebar';
import { PartnerDelegationBanner } from '@/components/partner-delegation-banner';
import { ScrollArea } from '@/components/ui/scroll-area';

export const Route = createFileRoute('/(protected)/partner')({
  component: PartnerLayout,
});

function PartnerLayout() {
  // Check if current organization is a partner
  const partnerInfoOptions = trpc.partner.getPartnerInfo.queryOptions();
  const partnerInfo = useQuery({
    ...partnerInfoOptions,
    retry: false, // Don't retry on error
  });

  // Handle authentication errors - redirect to login
  useEffect(() => {
    if (partnerInfo.error) {
      const error = partnerInfo.error as any;
      // Check if it's an UNAUTHORIZED error
      if (error?.data?.code === 'UNAUTHORIZED' || error?.message?.includes('UNAUTHORIZED') || error?.message?.includes('session-not-found')) {
        console.log('Partner route: Authentication error detected, redirecting to login');
        // Clear all auth state
        localStorage.clear();
        queryClient.clear();

        // Redirect to login without redirect_uri to avoid loops
        window.location.replace('/login');
      }
    }
  }, [partnerInfo.error]);

  const handleReturnToHome = async () => {
    try {
      // Check if there's delegation context
      const delegationContext = localStorage.getItem('partner:delegation');

      if (delegationContext) {
        const delegation = JSON.parse(delegationContext);
        const partnerOrgId = delegation?.partnerOrgId;

        if (partnerOrgId) {
          // Switch back to partner organization
          await trpcClient.organizations.setActiveOrganization.mutate({
            organizationId: partnerOrgId
          });
        }
      }

      // Clear all cached state
      localStorage.removeItem('auth:session');
      localStorage.removeItem('auth:activeOrg');
      localStorage.removeItem('auth:activeWorkspace');
      localStorage.removeItem('auth:activeWorkspaceId');
      localStorage.removeItem('partner:delegation');

      // Clear React Query cache
      queryClient.clear();

      // Navigate to home
      window.location.assign('/');
    } catch (error) {
      console.error('Failed to return to home:', error);
      // Fallback: just navigate to home
      window.location.assign('/');
    }
  };

  if (partnerInfo.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // RFC-091: Block access for non-partner organizations
  if (!partnerInfo.data?.partner?.summary) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset className="h-screen overflow-hidden">
          <div className="p-8 max-w-2xl mx-auto">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-3">
                  <p className="font-medium">Partner Dashboard Access Denied</p>
                  <p className="text-sm">
                    This organization is not configured as a partner.
                    Only partner organizations can access the partner dashboard.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Contact support if you believe you should have partner access.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReturnToHome}
                    className="mt-4"
                  >
                    <Home className="mr-2 h-4 w-4" />
                    Return to Home
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  return (
    <Suspense
      fallback={
        <div className='flex h-screen w-screen items-center justify-center'>
          <Loader2 className='h-4 w-4 animate-spin' />
        </div>
      }
    >
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset className='h-screen overflow-hidden'>
          <ScrollArea className='h-screen' viewClassName='[&>div]:!block'>
          <PartnerDelegationBanner />
          <Outlet />
          </ScrollArea>
        </SidebarInset>
      </SidebarProvider>
    </Suspense>
  );
}
